import { useCallback, useEffect, useMemo, useState } from "react";

import operationsService from "../api/operations.service";
import {
  OperationActionKey,
  OperationCreateForm,
  OperationFilters,
  OperationOptions,
  OperationRecord,
  OperationRecordsByTab,
  OperationSummary,
} from "../types";

const emptyRecords: OperationRecordsByTab = {
  requests: [],
  transfers: [],
  receipts: [],
  issues: [],
  returns: [],
  spareUsage: [],
  invoices: [],
};

const emptySummary: OperationSummary = {
  requests: 0,
  transfers: 0,
  receipts: 0,
  issues: 0,
  returns: 0,
  spareUsage: 0,
  invoices: 0,
  pendingActions: 0,
};

const emptyOptions: OperationOptions = {
  hubs: [],
  technicians: [],
  ames: [],
  spareParts: [],
  inventoryStocks: [],
  stockRequests: [],
  stockTransfers: [],
  serviceTickets: [],
};

export const useOperations = () => {
  const [records, setRecords] = useState<OperationRecordsByTab>(emptyRecords);
  const [summary, setSummary] = useState<OperationSummary>(emptySummary);
  const [options, setOptions] = useState<OperationOptions>(emptyOptions);
  const [filters, setFilters] = useState<OperationFilters>({
    tab: "requests",
    search: "",
    status: "",
    warehouseId: "",
    technicianId: "",
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchOperations = useCallback(
    async (nextFilters: OperationFilters = filters) => {
      try {
        setLoading(true);
        setError(null);
        const response = await operationsService.getOperations(nextFilters);
        setRecords(response.records);
        setSummary(response.summary);
      } catch (err) {
        console.error(err);
        setError("Failed to load warehouse operations.");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  useEffect(() => {
    operationsService
      .getOptions()
      .then(setOptions)
      .catch((err) => console.error(err));
  }, []);

  const refreshOptions = async () => {
    const nextOptions = await operationsService.getOptions();
    setOptions(nextOptions);
  };

  const updateFilters = (nextFilters: Partial<OperationFilters>) => {
    const updated = {
      ...filters,
      ...nextFilters,
    };

    setFilters(updated);
    fetchOperations(updated);
  };

  const runAction = async (
    record: OperationRecord,
    action: OperationActionKey,
    remarks: string,
    actorId: string
  ) => {
    try {
      setActionLoading(true);
      setError(null);
      await operationsService.runAction(record, action, remarks, actorId);
      setSuccess("Operation updated successfully.");
      await refreshOptions();
      await fetchOperations(filters);
    } catch (err) {
      console.error(err);
      const error = err as {
        response?: { data?: { message?: string; error?: string; errors?: Array<{ message?: string }> } };
      };
      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.errors?.[0]?.message ||
          "Unable to update this operation."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const createOperation = async (form: OperationCreateForm) => {
    try {
      setActionLoading(true);
      setError(null);
      await operationsService.createOperation(form);
      setSuccess("Operation created successfully.");
      await refreshOptions();
      await fetchOperations(filters);
    } catch (err) {
      console.error(err);
      const error = err as {
        response?: {
          data?: {
            message?: string;
            error?: string;
            errors?: Array<{ message?: string }>;
          };
        };
      };
      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.errors?.[0]?.message ||
          "Unable to create this operation."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const activeRows = useMemo(() => records[filters.tab] || [], [filters.tab, records]);

  return {
    records,
    activeRows,
    summary,
    options,
    filters,
    loading,
    actionLoading,
    error,
    success,
    refresh: fetchOperations,
    updateFilters,
    runAction,
    createOperation,
    clearError: () => setError(null),
    clearSuccess: () => setSuccess(null),
  };
};

export default useOperations;
